import {
    IHardwareNotificationSubscriptionGroupResponse,
    IHardwareNotificationSubscriptionResponse,
    IIndicatorNotificationSubscriptionResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { SimpleHierarchy } from 'components/hierarchy/SimpleHierarchy';
import { memo } from 'react';
import { excludeNullAndUndefined } from 'utils/array';

// 'Hierarchy' is optional, so this works for all subscription types
type FormattableSubscription = Pick<
    IIndicatorNotificationSubscriptionResponse,
    'Corporation' | 'Location' | 'Hierarchy'
>;

export type NotificationHierarchyProps = Readonly<{
    /**
     * The subscription that contains the hierarchy to display.
     */
    subscription: FormattableSubscription;
    /**
     * Whether to show the corporation in the path
     */
    showCorporation?: boolean;
    /**
     * Whether to show the location in the path
     */
    showLocation?: boolean;
}>;

export const NotificationSubscriptionHierarchy = memo(
    ({
        subscription,
        showCorporation = false,
        showLocation = false,
    }: NotificationHierarchyProps) => {
        const corporationName = showCorporation
            ? subscription.Corporation.CorporationName
            : undefined;
        const locationName = showLocation
            ? subscription.Location.LocationName
            : undefined;
        const groupNames = subscription.Hierarchy?.map(
            ({ HierarchyName }) => HierarchyName
        );
        return (
            <SimpleHierarchy
                tokens={excludeNullAndUndefined([
                    corporationName,
                    locationName,
                    ...(groupNames ?? []),
                ])}
            />
        );
    }
);

export type HardwareNotificationHierarchyProps = Readonly<{
    /**
     * The subscription that contains the hierarchy to display.
     */
    subscription:
        | IHardwareNotificationSubscriptionResponse
        | IHardwareNotificationSubscriptionGroupResponse;
    /**
     * Whether to display the corporation
     */
    showCorporation?: boolean;
}>;

export const HardwareNotificationSubscriptionHierarchy = memo(
    ({ subscription, showCorporation }: HardwareNotificationHierarchyProps) => {
        const corporationName = showCorporation
            ? subscription.Corporation.CorporationName
            : undefined;
        const locationName = subscription.Location.LocationName;

        return (
            <SimpleHierarchy
                tokens={excludeNullAndUndefined([corporationName, locationName])}
            />
        );
    }
);
